/** @jsxImportSource @emotion/react */
import { FILTER_METHOD_TYPES } from "../../../../actions/Layers/actions";
import { Check, FruitProps, Layer, RipeningRoom } from "../../../../actions/Layers/constants";
import { CategoryType, FormOption, RootConfigType } from "../../../../actions/Tenants/config/constantsTyped";
import { find_category } from "../../../../actions/Tenants/config/utils";
import { User } from "../../../../actions/Users/actions";
import { isFilledArray } from "../../../../utils";
import { capitalizeFirstLetter } from "../../../../utils/tools";
import { TenantDefect } from "../../../Forms/useManagedOptionsHook";
import { CategoryColorBadge } from "../../../Helper/Badges";
import { ColorCodedVector } from "../../Helper/ColorCodedSpan";
import { hasHebrewChars, truncateText } from "../Layer/Report/utils";


export const SORTING_BACKENDS = ["layer_meta", "layer", "check", "latest_location"];
// export const FILTER_BACKENDS = ["layer_meta"];


export interface FormOptions {
    [key: string]: FormOption[]
}

export interface DataSource {
    config: RootConfigType;
    layer: Layer;
    check: Check | null; // * To be used in check tables and layer overview
    fruit: FruitProps | null; // * To be used in fruit tables
    // aggregated_fruit: AggregatedFruit | null; // * To be used in  check tables and layer overview
    children?: Layer[] | {
        results: Layer[]
        count: number
    } | null; // * To be used in layer overview
    form_options: FormOptions;
    ripeningRoom?: RipeningRoom,
    user?: User;
    tenantDefectDefinitions?: TenantDefect[];
}


export interface FieldOptions {
    fieldname_getter?: string;
    fieldname_setter?: string;
    formatters?: ((value: string | number | null | undefined) => string)[];
    string_when_empty?: string;
    append_key?: string;
    color_code_field?: string | false;
    value_filter?: (value: any, _datasource: DataSource) => any; // Note the underscore before datasource
    filter_config?: FilterConfig;
    excluded_from_download?: boolean;
}

interface FilterConfig {
    formatter: (value: string | number | null) => string;
    decimals?: number;
    unity?: string;
    value_if_null?: string;
    datetime_format?: string;
    filter_method_type: string;
    min?: number;
    max?: number;
    options_name?: string;
}

class Field {
    field_type = "general_field";

    backend_table = "layer";

    field_config: any = {};

    fieldname_getter: string;

    options_name?: string;

    fieldname_setter: string;

    config: RootConfigType | undefined;

    label: string;

    append_key: string; // TODO: not needed anymore

    value: string | number | null | undefined = null;

    datasource: DataSource | undefined;

    footer: any = null;

    string_when_empty = "-";

    flag_class = "";

    flag_color = "";

    classname = "";

    categories: CategoryType[] = [];

    excluded_from_download?: boolean;

    category_name?: string;

    higher_value_is_more_ripe?: boolean;

    default_log_scale?: boolean;

    filter_config: FilterConfig = {
        formatter: (value: string | number | null) => value as string,
        filter_method_type: FILTER_METHOD_TYPES.TEXT,
    };

    formatters: ((value: string | number | null | undefined) => string)[];

    value_filter: (value: any, _datasource: DataSource) => any;

    constructor(
        fieldname: string,
        label: string,
        options: FieldOptions = {}
    ) {
        const {
            fieldname_getter = fieldname,
            fieldname_setter = fieldname,
            formatters = [],
            string_when_empty = "-",
            append_key = "",
            excluded_from_download = false,
            value_filter = (value: any, _: DataSource) => value, // eslint-disable-line @typescript-eslint/no-unused-vars
            filter_config = undefined,
        } = options;

        this.fieldname_getter = fieldname_getter;
        this.fieldname_setter = fieldname_setter;
        this.label = label;
        this.formatters = formatters;
        this.string_when_empty = string_when_empty;
        this.value_filter = value_filter;
        this.append_key = append_key;
        this.excluded_from_download = excluded_from_download;
        if (filter_config) {
            this.filter_config = { ...this.filter_config, ...filter_config };
        }
    }

    get_id(): string {
        const key = `${this.field_type}_${this.fieldname_getter}_${this.label}`;
        if (this.append_key) {
            return `${key}_${this.append_key}`;
        }
        return key;
    }

    get_backend_fieldname(): string[] {
        const fields = [`${this.backend_table}.${this.fieldname_getter}`];
        const color_code_field = this.field_config?.color_code_field;
        if (color_code_field) {
            fields.push(`${this.backend_table}.${color_code_field}`);
        }
        return fields;
    }

    is_sortable(): boolean {
        return SORTING_BACKENDS.includes(this.backend_table);
    }

    is_filterable(): boolean {
        return false;
    }

    set_filter_type_for_formatter(formatter_key, { formatter }): this {
        switch (formatter_key) {
        case "toFixed":
            this.filter_config = {
                formatter,
                min: 0,
                max: 100,
                filter_method_type: FILTER_METHOD_TYPES.NUMBER_RANGE,
            };
            break;


        case "formatDate":
            this.filter_config = {
                formatter,
                filter_method_type: FILTER_METHOD_TYPES.DATE_RANGE,
            };
            break;
        case "formatDateTime":
            this.filter_config = {
                formatter,
                filter_method_type: FILTER_METHOD_TYPES.DATE_RANGE,
            };
            break;
        case "metricPercentage":
            this.filter_config = {
                formatter,
                min: 0,
                max: 1,
                filter_method_type: FILTER_METHOD_TYPES.NUMBER_RANGE,
            };
            break;

        default:

        }
        return this;
    }

    // * set the value from one of the the objects
    set_value(datasource: DataSource): this {
        // always reset the value
        this.value = null;
        this.value = this.value_filter(datasource.layer[this.fieldname_getter], datasource);
        return this;
    }

    // * value as retrieved from the api
    get_value(): any {
        return this.value;
    }

    clone(): this {
        const clonedInstance = Object.create(this);
        // Copy the properties from the current instance to the cloned instance
        Object.assign(clonedInstance, this);
        return clonedInstance;
    }

    // * set the fieldname for setting on the object
    set_fieldname_setter(new_fieldname: string): this {
        this.fieldname_setter = new_fieldname;
        return this;
    }

    // * set the fieldname for setting on the object
    set_fieldname_getter(new_fieldname: string): this {
        this.fieldname_getter = new_fieldname;
        return this;
    }

    set_color_code(object: any): void {
        // * Set flag color to be used in vector components
        const color_code_field = this.field_config?.color_code_field;
        this.flag_color = color_code_field ? object?.[color_code_field] : "";
        // * Set flag class to be used in Reactstrap components
        this.flag_class = color_code_field ? `text-${object?.[color_code_field]}` : "";
    }

    set_classname(classname: string): void {
        this.classname = classname;
    }

    get_classname(): string {
        return `${this.classname} ${this.flag_class}`;
    }

    // * set the label
    set_label(new_label: string): this {
        // TODO: to be solved in the wizard (every tenant should call NEW)
        const clonedInstance = Object.create(this);
        // Copy the properties from the current instance to the cloned instance
        Object.assign(clonedInstance, this);
        clonedInstance.label = new_label; // Set the new label on the cloned instance

        return clonedInstance;
    }

    // try to get a label for this form option value
    get_form_option_label(value): string {
        const key = this.options_name || this.fieldname_getter;
        const form_options = this.config?.form_options || {};

        if (key in form_options) {
            const option = form_options[key].find((option: FormOption) => option.value === value);
            return option ? option.label : value;

        }
        return value;

    }

    // * set a single option
    set_option(option: string, value: any): this {
        (this as any)[option] = value; // Type assertion for dynamic property access
        return this;
    }

    // * set multiple options at once
    set_options(options: { [key: string]: any }): this {
        Object.entries(options).forEach(([option, value]) => {
            this.set_option(option, value);
        });
        return this;
    }

    // * dynamicly add a formatter
    add_formatter(formatter: ((value: string | number | null | undefined) => string)): this {
        this.formatters.push(formatter);
        return this;
    }

    // * format the value by looping over all the formatters
    formatted_value(value: any = null): string {
        value = value || this.value;
        if (this.formatters.length === 0) {
            if (isFilledArray(this.field_config?.categories)) {
                return find_category(value, this.field_config?.categories)?.label || this.get_form_option_label(value);
            }
            return this.get_form_option_label(value);
        }
        return this.format_value(value) || this.string_when_empty;
    }

    format_value(value: any): string {
        return this.formatters.reduce((acc, formatter) => formatter(acc), value);
    }

    // * render react component, can be overwritten by special fields
    display_component(): JSX.Element {
        if (isFilledArray(this.field_config?.categories) && this.formatters.length === 0) {
            return <CategoryColorBadge value={this.value as string | number} categories={this.field_config?.categories} />;
        }
        return <span className={this.get_classname()}>{this.formatted_value()}</span>;
    }

    display_vector(size = 7, maxLength?: number, truncateLength?: number): JSX.Element {
        if (!size) {
            size = 7;
        }

        let text = capitalizeFirstLetter(this.formatted_value());

        if (maxLength && truncateLength) {
            text = truncateText(text, maxLength, truncateLength);
        }

        const fontFamily = hasHebrewChars(text) ? "PDF-Hebrew" : "Poppins";

        return <ColorCodedVector flag={this.flag_color} style={{ fontSize: size, fontFamily }}>{text}</ColorCodedVector>;

    }

    // * set excluded from download
    set_excluded_from_download(excluded_from_download: boolean): this {
        this.excluded_from_download = excluded_from_download;
        return this;
    }

}

export default Field;
